import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listEstates, deleteEstate } from '../../api/estates';
import { useTenant } from '../../contexts/TenantContext';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import ResponsiveList from '../../components/common/ResponsiveList';

const EstateList = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estates, setEstates] = useState([]);
  const [totalEstates, setTotalEstates] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState(null);

  // Fetch estates
  const fetchEstates = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (tenantId) params.tenant_id = tenantId;

      const data = await listEstates(params);
      setEstates(data.estates || []);
      setTotalEstates(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching estates:', error);
      setError('Failed to load estates. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEstates();
  }, [page, search, tenantId]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
  };

  // Handle create estate
  const handleCreateEstate = () => {
    navigate('/members/estates/create');
  };

  // Handle edit estate
  const handleEditEstate = (estate, e) => {
    e.stopPropagation();
    navigate(`/members/estates/edit/${estate.id}`);
  };

  // Handle row click
  const handleRowClick = (estate) => {
    navigate(`/members/estates/${estate.id}`);
  };

  // Handle delete estate
  const handleDeleteEstate = (estate, e) => {
    e.stopPropagation();
    setSelectedEstate(estate);
    setOpenDialog(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    setIsLoading(true);

    try {
      await deleteEstate(selectedEstate.id);
      fetchEstates();
    } catch (error) {
      console.error('Error deleting estate:', error);
      setError('Failed to delete estate. Please try again later.');
    } finally {
      setIsLoading(false);
      setOpenDialog(false);
    }
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (isLoading && estates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters={embedded}>
      <Box sx={embedded ? {} : { my: 4 }}>
        {!embedded && (
          <Typography variant="h4" component="h1" gutterBottom>
            Estates
          </Typography>
        )}

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            label="Search Estates"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateEstate}
          >
            Add Estate
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!embedded && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Bulk import/export of estates is handled together with members on the{' '}
            <strong>FPA Members</strong> page — use its Download / Import buttons. The
            workbook includes an <strong>Estates</strong> sheet covering every estate field.
          </Alert>
        )}

        <ResponsiveList
          items={estates}
          loading={isLoading}
          empty="No estates found."
          getKey={(e) => e.id}
          onRowClick={(e) => handleRowClick(e)}
          columns={[
            { key: 'name', header: 'Name', render: (e) => e.name },
            {
              key: 'billing',
              header: 'Billing Mode',
              render: (e) => (
                <Chip
                  label={e.billing_mode === 'estate_pays' ? 'Estate Pays' : 'Members Pay'}
                  color={e.billing_mode === 'estate_pays' ? 'primary' : 'default'}
                  size="small"
                />
              ),
            },
            { key: 'members', header: 'Member Count', render: (e) => e.member_count || 0 },
            { key: 'contact', header: 'Contact', render: (e) => e.contact_name || '-' },
            {
              key: 'actions',
              header: 'Actions',
              render: (e) => (
                <Box onClick={(ev) => ev.stopPropagation()}>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={(ev) => handleEditEstate(e, ev)} sx={{ mr: 1 }}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={(ev) => handleDeleteEstate(e, ev)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            },
          ]}
          mobileCard={(e) => (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{e.name}</Typography>
                  <Chip
                    label={e.billing_mode === 'estate_pays' ? 'Estate Pays' : 'Members Pay'}
                    color={e.billing_mode === 'estate_pays' ? 'primary' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box onClick={(ev) => ev.stopPropagation()}>
                  <IconButton size="small" color="primary" onClick={(ev) => handleEditEstate(e, ev)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={(ev) => handleDeleteEstate(e, ev)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {e.member_count || 0} member{(e.member_count || 0) === 1 ? '' : 's'}
                {e.contact_name && ` · ${e.contact_name}`}
              </Typography>
            </Box>
          )}
        />

        {estates.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Showing {estates.length} of {totalEstates} estates
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Delete Estate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete estate "{selectedEstate?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EstateList;
